import React, { useEffect, useState } from "react";
import MobileWrapper from "../../components/MobileWrapper";
import { getAllDocsSnap } from "../../utils/firebase/Firebase.utils";
import {
  FaUserFriends,
  FaCalendarAlt,
  FaSearch,
  FaHome,
  FaClock,
  FaTimesCircle,
  FaCheckCircle,
  FaUserClock,
  FaUserCheck,
  FaArrowRight,
  FaPlus,
} from "react-icons/fa";

export default function Guests() {
  const [guests, setGuests] = useState([]);
  const [filteredGuests, setFilteredGuests] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchGuests = async () => {
      await getAllDocsSnap("guests", (snap) => {
        //arrange using updatedAt field
        snap.sort((a, b) => {
          const dateA = new Date(a.updatedAt || a.requestDate).getTime();
          const dateB = new Date(b.updatedAt || b.requestDate).getTime();
          return dateB - dateA; // Sort in descending order
        });
        setGuests(snap);
        setFilteredGuests(snap);
        setIsLoading(false);
      });
    };
    fetchGuests();
  }, []);

  // Apply both search term and status filter
  useEffect(() => {
    let filtered = [...guests];

    // Apply status filter first
    if (activeFilter !== "all") {
      filtered = filtered.filter(
        (guest) => guest.status?.toLowerCase() === activeFilter.toLowerCase()
      );
    }

    // Then apply search term filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter((guest) => {
        // Search in requestor name
        if (guest.requestorName?.toLowerCase().includes(term)) return true;

        // Search in unit identifiers
        if (
          guest.linkedIdentifiers?.some((unit) =>
            unit.toLowerCase().includes(term)
          )
        )
          return true;

        // Search in visitor names
        if (
          guest.guests?.some((visitor) =>
            visitor.name?.toLowerCase().includes(term)
          )
        )
          return true;

        // Search in plate numbers
        if (
          guest.guests?.some((visitor) =>
            visitor.plateNumber?.toLowerCase().includes(term)
          )
        )
          return true;

        return false;
      });
    }

    setFilteredGuests(filtered);
  }, [searchTerm, guests, activeFilter]);

  // Color scheme based on colorscheme.md
  const eleghoBlue = "#363062";
  const eleghoGreen = "#8FCD00";

  // Function to format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return eleghoGreen;
      case "pending":
        return "#FFA500"; // Orange
      case "rejected":
        return "#FF0000"; // Red
      case "admitted":
        return "#4299E1"; // Blue
      case "completed":
        return "#805AD5"; // Purple
      case "returned":
        return "#718096"; // Gray
      default:
        return "#808080"; // Default Gray
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return <FaCheckCircle />;
      case "pending":
        return <FaClock />;
      case "rejected":
        return <FaTimesCircle />;
      case "admitted":
        return <FaUserCheck />;
      case "completed":
        return <FaCheckCircle />;
      case "returned":
        return <FaArrowRight />;
      default:
        return <FaClock />;
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleAddNewRequest = () => {
    // Navigate to the new guest request form
    window.location.href = "/guests/new";
  };

  const handleFilterChange = (filter) => {
    if (filter === activeFilter) {
      // If clicking the active filter, clear it
      setActiveFilter("all");
    } else {
      setActiveFilter(filter);
    }
  };

  // Count for each status type
  const getStatusCount = (status) => {
    return guests.filter(
      (guest) => guest.status?.toLowerCase() === status.toLowerCase()
    ).length;
  };

  const handleCardClick = (id) => {
    window.location.href = `guest-details/${id}`;
  };

  if (isLoading) {
    return (
      <MobileWrapper label="Guests Requests">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#363062]"></div>
            <p className="mt-2 text-sm text-[#363062]">
              Loading guest requests...
            </p>
          </div>
        </div>
      </MobileWrapper>
    );
  }

  return (
    <MobileWrapper label="Guests Requests">
      <div className="w-full mx-auto px-4 max-w-4xl">
        {/* Header Section */}
        <div className="flex items-center justify-between mb-6 bg-[#363062]/5 p-4 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-[#363062] rounded-lg">
              <FaUserFriends className="text-white text-base" />
            </div>
            <div>
              <h1 className="text-sm font-medium text-[#363062]">
                Guest Registration
              </h1>
              <p className="text-xs text-gray-500">
                Manage visitor access and approvals
              </p>
            </div>
          </div>
          <button
            className="flex items-center px-3 py-2 text-xs font-medium text-white rounded-md bg-[#8FCD00] shadow-sm hover:bg-[#7db700] transition-colors"
            onClick={handleAddNewRequest}
          >
            <FaPlus className="w-3 h-3 mr-1.5" />
            New
          </button>
        </div>

        {/* Search Bar */}
        <div className="relative mb-4">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <FaSearch className="w-3.5 h-3.5 text-gray-500" />
          </div>
          <input
            type="text"
            className="block w-full p-2.5 pl-10 text-xs border rounded-lg bg-gray-50 focus:outline-none focus:ring-1 focus:ring-[#363062] focus:border-[#363062]"
            placeholder="Search by name, unit, or status..."
            value={searchTerm}
            onChange={handleSearch}
            style={{ borderColor: "#E2E8F0" }}
          />
          {searchTerm && (
            <button
              className="absolute inset-y-0 right-0 flex items-center pr-3"
              onClick={() => setSearchTerm("")}
            >
              <FaTimesCircle className="w-3.5 h-3.5 text-gray-500" />
            </button>
          )}
        </div>

        {/* Status Filter Buttons */}
        <div className="mb-4 overflow-x-auto">
          <div className="flex space-x-2 pb-2">
            <button
              className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                activeFilter === "all"
                  ? "bg-[#363062] text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
              onClick={() => handleFilterChange("all")}
            >
              All ({guests.length})
            </button>
            <button
              className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                activeFilter === "pending"
                  ? "bg-[#FFA500] text-white"
                  : "bg-[#FFA500]/10 text-gray-700 hover:bg-[#FFA500]/20"
              }`}
              onClick={() => handleFilterChange("pending")}
            >
              Pending ({getStatusCount("pending")})
            </button>
            <button
              className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                activeFilter === "approved"
                  ? "bg-[#8FCD00] text-white"
                  : "bg-[#8FCD00]/10 text-gray-700 hover:bg-[#8FCD00]/20"
              }`}
              onClick={() => handleFilterChange("approved")}
            >
              Approved ({getStatusCount("approved")})
            </button>
            <button
              className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                activeFilter === "admitted"
                  ? "bg-[#4299E1] text-white"
                  : "bg-[#4299E1]/10 text-gray-700 hover:bg-[#4299E1]/20"
              }`}
              onClick={() => handleFilterChange("admitted")}
            >
              Admitted ({getStatusCount("admitted")})
            </button>
          </div>
          <div className="flex space-x-2">
            <button
              className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                activeFilter === "rejected"
                  ? "bg-[#FF0000] text-white"
                  : "bg-[#FF0000]/10 text-gray-700 hover:bg-[#FF0000]/20"
              }`}
              onClick={() => handleFilterChange("rejected")}
            >
              Rejected ({getStatusCount("rejected")})
            </button>
            <button
              className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                activeFilter === "completed"
                  ? "bg-[#805AD5] text-white"
                  : "bg-[#805AD5]/10 text-gray-700 hover:bg-[#805AD5]/20"
              }`}
              onClick={() => handleFilterChange("completed")}
            >
              Completed ({getStatusCount("completed")})
            </button>
            <button
              className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                activeFilter === "returned"
                  ? "bg-[#718096] text-white"
                  : "bg-[#718096]/10 text-gray-700 hover:bg-[#718096]/20"
              }`}
              onClick={() => handleFilterChange("returned")}
            >
              Returned ({getStatusCount("returned")})
            </button>
          </div>
        </div>

        {/* Guest Request Cards */}
        <div className="mt-4 space-y-3">
          {filteredGuests.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-100">
              <div className="p-3 bg-[#363062]/10 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                <FaUserFriends className="text-[#363062] text-lg" />
              </div>
              <h3 className="text-sm font-medium text-[#363062] mb-1">
                {guests.length === 0
                  ? "No Guest Requests"
                  : "No Matching Results"}
              </h3>
              <p className="text-xs text-gray-500">
                {guests.length === 0
                  ? "No guest requests have been submitted yet"
                  : "Try adjusting your filters or search terms"}
              </p>
            </div>
          ) : (
            filteredGuests.map((guest) => (
              <div
                key={guest.id}
                className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden transition-all hover:shadow-md hover:translate-y-[-2px] cursor-pointer"
                onClick={() => handleCardClick(guest.id)}
              >
                <div className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div className="text-sm font-medium text-[#363062]">
                      {guest.requestorName || "Unnamed Requestor"}
                    </div>
                    <div
                      className="flex items-center px-2 py-1 rounded-full text-xs font-medium gap-1.5"
                      style={{
                        backgroundColor: `${getStatusColor(guest.status)}15`,
                        color: getStatusColor(guest.status),
                      }}
                    >
                      <span className="text-xs">
                        {getStatusIcon(guest.status)}
                      </span>
                      {guest.status || "Unknown"}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mb-3">
                    <div className="flex items-center gap-1.5">
                      <FaCalendarAlt className="text-gray-400 flex-shrink-0" />
                      <span>{formatDate(guest.date || guest.requestDate)}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <FaClock className="text-gray-400 flex-shrink-0" />
                      <span>{guest.time || "N/A"}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <FaHome className="text-gray-400 flex-shrink-0" />
                      <span>
                        {guest.linkedIdentifiers
                          ? guest.linkedIdentifiers.join(", ")
                          : "N/A"}
                      </span>
                    </div>
                    {guest.assignedTo && guest.assignedTo.length > 0 && (
                      <div className="flex items-center gap-1.5">
                        <FaUserClock className="text-gray-400 flex-shrink-0" />
                        <span>
                          {guest.assignedTo.map((a) => a.label).join(", ")}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Visitors Section */}
                  {guest.guests && guest.guests.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-100">
                      <div className="text-xs text-gray-500 mb-1.5">
                        Visitors:
                      </div>
                      <div className="space-y-1">
                        {guest.guests.slice(0, 2).map((visitor, i) => (
                          <div
                            key={i}
                            className="flex items-center gap-1 text-xs"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-[#363062]"></span>
                            <span>{visitor.name || "Unnamed Visitor"}</span>
                            {visitor.plateNumber && (
                              <span className="text-gray-400 ml-1">
                                ({visitor.plateNumber})
                              </span>
                            )}
                          </div>
                        ))}
                        {guest.guests.length > 2 && (
                          <div className="text-xs text-[#363062]">
                            +{guest.guests.length - 2} more
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {/* Footer with View Details */}
                <div className="flex justify-end items-center py-2 px-4 bg-gray-50 border-t border-gray-100">
                  <div className="flex items-center text-xs font-medium text-[#363062]">
                    View Details
                    <FaArrowRight className="ml-1.5 w-3 h-3" />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </MobileWrapper>
  );
}
